/**
 * Two-letter initials from one free-text name — the avatar fallback for anything
 * carrying a single display string rather than split fields: "John Doe" → 'JD',
 * "Cher" → 'C'.
 *
 * Empty segments are dropped *before* the two-word slice, not after. A name with
 * a leading or doubled space — "Acme  Corp", straight out of a user-entered
 * business name — would otherwise spend both slots on blanks and render an empty
 * avatar.
 *
 * For separate first/last fields use `getInitialsFromName`, which also takes a
 * contextual fallback for contacts that have neither.
 */
export function getInitials(name: string): string {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join('');
}

/**
 * Two-letter initials from separate first/last name fields — the avatar
 * fallback used across contacts, the inbox, and teammate pickers. Returns
 * `fallback` (default `'?'`) when neither name yields a character, so callers
 * can supply a contextual fallback such as a phone tail or email initial:
 *
 *   getInitialsFromName('John', 'Doe')                 → 'JD'
 *   getInitialsFromName('John')                        → 'J'
 *   getInitialsFromName(null, null)                    → '?'
 *   getInitialsFromName('', '', phone.slice(-2))       → last 2 phone digits
 */
export function getInitialsFromName(
    firstName?: string | null,
    lastName?: string | null,
    fallback = '?',
): string {
    const first = (firstName ?? '').trim().charAt(0);
    const last = (lastName ?? '').trim().charAt(0);
    return `${first}${last}`.toUpperCase() || fallback;
}

/**
 * Joins name parts for display, skipping any that are missing.
 *
 * Both parts are nullable: a contact created from a booking or web form has a
 * single "Name" field, so `lastName` is null for anyone who entered one word.
 * Interpolating that directly (`${firstName} ${lastName}`) renders the literal
 * string "null" — and TypeScript cannot catch it, because `${null}` is legal.
 * Always join through here.
 *
 *   getFullName('John', 'Doe')  → 'John Doe'
 *   getFullName('Harsh', null)  → 'Harsh'
 */
export function getFullName(firstName?: string | null, lastName?: string | null): string {
    return [firstName, lastName].filter(Boolean).join(' ').trim();
}

/**
 * Per-part cap. Matches `@MaxLength(NAME_PART_MAX_LENGTH)` on
 * `firstName`/`lastName` in every user DTO — register, register-via-invitation
 * and update-user — and the width of the `VarChar(100)` columns behind them.
 */
export const NAME_PART_MAX_LENGTH = 100;

/**
 * Cap for the single free-text "Name" field the forms actually show — sign-up,
 * the invite drawer and the profile page all collect one value and split it
 * with `splitFullName` at the API boundary.
 *
 * Deliberately the per-part cap and not twice it: `firstName` and `lastName` are
 * slices of this one string, so a name that fits this limit can never split into
 * a part that overflows its column. That leaves the user one number they can see
 * coming. The old two-part rule moved with the position of their first space —
 * "Ana" plus a 60-character surname was rejected while a 90-character single
 * word sailed through, with an error that talked about fields the form does not
 * have.
 */
export const FULL_NAME_MAX_LENGTH = NAME_PART_MAX_LENGTH;

/**
 * Control characters, replaced by a space rather than deleted.
 *
 * They are separators: a multi-line paste of "John\nvan\nDoe" must normalise to
 * "John van Doe", not "JohnvanDoe". Left in place, the newline would reach
 * storage inside `lastName` and break single-line rendering and CSV export.
 */
// eslint-disable-next-line no-control-regex -- matching control characters is the point here
const NAME_CONTROL_CHARS_REGEX = /[\u0000-\u001F\u007F-\u009F]/g;

/**
 * Invisible characters, deleted outright — they separate nothing.
 *
 *   - U+200B zero-width space, U+FEFF BOM, U+00AD soft hyphen. None of these are
 *     whitespace to `trim()`, so a name consisting only of one satisfies a
 *     `min(1)` rule while rendering as blank everywhere.
 *   - U+202A–202E and U+2066–2069, the bidi overrides and isolates. These
 *     reverse the rendering of surrounding text, so a name carrying one can
 *     misrepresent every member list and mention chip it appears in.
 *
 * Deliberately NOT the whole `\p{Cf}` class: that also contains ZWNJ (U+200C)
 * and ZWJ (U+200D), which are load-bearing in Persian and Hindi names. Stripping
 * those would silently corrupt legitimate input.
 */
const NAME_INVISIBLE_CHARS_REGEX = /[\u00AD\u200B\u202A-\u202E\u2066-\u2069\uFEFF]/g;

/** A name must still hold one letter or digit once the invisibles are gone. */
export const NAME_HAS_LETTER_OR_DIGIT_REGEX = /[\p{L}\p{N}]/u;

/**
 * Clean a user-entered name: control characters to spaces, invisible characters
 * dropped, whitespace runs collapsed, then trimmed.
 *
 * Repair, not rejection. This runs as a Zod `transform`, so a legacy name that
 * picked up a stray zero-width space is fixed on the next save rather than
 * locking its owner out of every profile change — the trap the old `min(2)`
 * surname rule created, described in `profile-page.tsx`. The single hard failure
 * is a name left with no letter or digit at all; assert that separately with
 * `NAME_HAS_LETTER_OR_DIGIT_REGEX`.
 *
 * Kept in step with `normalizeName` in
 * `backend/src/common/validators/format.validator.ts`; both sides run the same
 * case table, so drift fails a test rather than reaching production.
 */
export function normalizeUserName(value: string): string {
    return value
        .replace(NAME_CONTROL_CHARS_REGEX, ' ')
        .replace(NAME_INVISIBLE_CHARS_REGEX, '')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Split one free-text name into the first/last pair the API expects, on the
 * first whitespace run — "Thomas Müller" → Thomas / Müller, "Cher" → Cher / ''.
 * Everything after that first space stays in `lastName`, so multi-word surnames
 * ("Ana Maria de Souza" → Ana / Maria de Souza) survive intact.
 *
 * Exact inverse of `getFullName`, which is what makes a single "Full Name" input
 * safe over split storage: split-then-join returns the original string, so a
 * round trip through the form never loses characters or re-orders them.
 */
export function splitFullName(fullName: string | null | undefined): {
    firstName: string;
    lastName: string;
} {
    const trimmed = fullName?.trim() ?? '';
    if (!trimmed) return { firstName: '', lastName: '' };

    const separatorIndex = trimmed.search(/\s/);
    if (separatorIndex === -1) return { firstName: trimmed, lastName: '' };

    return {
        firstName: trimmed.slice(0, separatorIndex),
        lastName: trimmed.slice(separatorIndex + 1).trim(),
    };
}

/**
 * Cap a string at `maxLength`, appending an ellipsis when it had to cut.
 *
 * For values that end up interpolated into a *sentence* — a dialog description,
 * a placeholder — where the `Truncate` component cannot be used because the
 * surrounding element is a `<p>` and `Truncate`'s root is a `<div>`. Anywhere
 * the value stands alone, prefer `Truncate`: it ellipsises to the real rendered
 * width and reveals the full value on hover.
 */
export function truncateText(value: string, maxLength: number): string {
    return value.length > maxLength ? `${value.slice(0, maxLength).trimEnd()}…` : value;
}

export function capitalize(value: string): string {
    return value
        .toLowerCase()
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

/**
 * Convert a personal-name string to Title Case — capitalises the first
 * letter of every space-separated word and lower-cases the rest. Handles
 * empty/whitespace-only inputs gracefully.
 *
 * "john doe"   → "John Doe"
 * "MARY-ANN"   → "Mary-Ann" (hyphens preserved, first letter of each segment)
 */
export function toTitleCase(value: string | null | undefined): string {
    if (!value) return '';
    return value
        .trim()
        .split(/\s+/)
        .map((word) =>
            word
                .split('-')
                .map((segment) =>
                    segment.length === 0
                        ? segment
                        : segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase(),
                )
                .join('-'),
        )
        .join(' ');
}
